import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { listSubjects, listStaff, TEACHER_DEPARTMENT, getLatestPublishedRun, getTimetableRun } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// "Giáo viên tổ hợp tự nhiên/xã hội" — danh sách GV ĐANG CÓ TIẾT THẬT trong
// TKB tuần hiện tại (bản công bố gần nhất — cùng quy ước "tuần hiện tại" mà
// trang "TKB của tôi" dùng, xem ghi chú ở published-runs/latest) và tiết đó
// thuộc nhóm môn tương ứng. Không lấy từ PCCM gốc nữa (yêu cầu rõ: "chỉ
// hiện các giáo viên có trong TKB tuần hiện tại" — PCCM có thể có GV chưa
// xếp được tiết nào, không nên hiện). Môn nào thuộc nhóm nào CỐ ĐỊNH theo
// tên môn (Tự nhiên = Toán, Vật lý, Hóa học, Sinh học, Công nghệ; Xã hội =
// các môn còn lại, TRỪ các môn không thuộc tổ hợp nào — vd HĐTN(SH) là hoạt
// động sinh hoạt lớp, không phải môn thi/chọn tổ hợp).
const GROUP_LABELS = { tunhien: "Tổ hợp tự nhiên", xahoi: "Tổ hợp xã hội" };
const TUNHIEN_SUBJECTS = ["Toán", "Vật lý", "Hóa học", "Sinh học", "Công nghệ"];
const NOT_A_COMBO_SUBJECT = ["HĐTN(SH)"];

const HappySchoolCombinationTeachers = () => {
  const { group } = useParams(); // "tunhien" | "xahoi"
  const groupLabel = GROUP_LABELS[group] || "Tổ hợp";
  const schoolId = getActiveSchoolId();
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [grid, setGrid] = useState(null);
  const [week, setWeek] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    Promise.all([listSubjects(schoolId), listStaff(schoolId, { department: TEACHER_DEPARTMENT }), getLatestPublishedRun(schoolId)])
      .then(async ([s, t, pub]) => {
        setSubjects(s);
        setTeachers(t);
        if (!pub) { setGrid({}); setWeek(null); return; }
        setWeek(pub);
        const run = await getTimetableRun(schoolId, pub.runId);
        setGrid(run.grid || {});
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s])), [subjects]);
  const isInGroup = (subjectName) => {
    if (NOT_A_COMBO_SUBJECT.includes(subjectName)) return false;
    return (group === "tunhien") === TUNHIEN_SUBJECTS.includes(subjectName || "");
  };

  const teacherReport = useMemo(() => {
    if (!grid) return [];
    const byTeacher = {};
    Object.values(grid).forEach((cell) => {
      if (!cell || !cell[0] || !cell[1]) return;
      const [subjectId, teacherId] = cell;
      const subj = subjectById[subjectId];
      if (!subj || !isInGroup(subj.name)) return;
      const t = teachers.find((x) => x.id === teacherId);
      if (!t) return;
      const rec = (byTeacher[teacherId] = byTeacher[teacherId] || {
        name: t.name,
        phone: t.phone || "",
        employmentType: t.employmentType || "",
        subjectNames: new Set(),
      });
      rec.subjectNames.add(subj.name);
    });
    return Object.values(byTeacher)
      .map((r) => ({ ...r, subjectNames: [...r.subjectNames].join(", ") }))
      .sort((a, b) => a.name.localeCompare(b.name, "vi"));
  }, [grid, teachers, subjectById, group]);

  if (!schoolId) {
    return (
      <div style={{ padding: 16 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: "0 auto" }}>
      <p style={{ margin: "0 0 6px" }}>
        <Link to="/happyschool/teaching" style={{ fontSize: 13, color: "#0ea5a3" }}>← Quản lý dạy học</Link>
      </p>
      <h2 style={{ margin: "0 0 8px 0" }}>Giáo viên {groupLabel.toLowerCase()}</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        GV đang có tiết {group === "tunhien" ? TUNHIEN_SUBJECTS.join(", ") : "các môn còn lại"} trong TKB tuần hiện tại
        {week ? ` (${week.startDate}–${week.endDate}${week.name ? ", " + week.name : ""})` : ""}.
      </p>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : !week ? (
        <p style={{ color: "#6b7280" }}>Chưa có bản TKB nào được công bố.</p>
      ) : teacherReport.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Chưa GV nào có tiết thuộc nhóm này trong TKB tuần hiện tại.</p>
      ) : (
        <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle}>Tên giáo viên</th>
              <th style={thStyle}>SĐT</th>
              <th style={thStyle}>Loại hình</th>
              <th style={thStyle}>Môn dạy</th>
            </tr>
          </thead>
          <tbody>
            {teacherReport.map((r) => (
              <tr key={r.name + r.phone}>
                <td style={tdNameStyle}>{r.name}</td>
                <td style={tdStyle}>{r.phone || "—"}</td>
                <td style={tdStyle}>{r.employmentType || "—"}</td>
                <td style={tdStyle}>{r.subjectNames}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const thStyle = { border: "1px solid #e5e7eb", padding: "6px 10px", background: "#f9fafb", textAlign: "left", whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #e5e7eb", padding: "6px 10px" };
const tdNameStyle = { ...tdStyle, fontWeight: 600, whiteSpace: "nowrap" };

export default HappySchoolCombinationTeachers;
